import isNil from 'lodash/isNil';
import JSURL from 'jsurl';

import {identity} from 'common/util/function';
import {rison} from 'common/util/rison';

const JS_URL_TRANSFORMS = [
  // try identity function first, in the event that given url is valid
  identity,
  // if there is a JSURL but it is invalid make sure it is surrounded by ~( ... ). This can be stripped by some sites
  (jsurl) => `~(${jsurl.replace(/^~\(|\)$/g, ``)})`,
  // Gmail strips out both trailing single quotes and right parentheses
  (jsurl) => `~(${jsurl.replace(/^~\(|\)$/g, ``)}')`,
];

const JS_URL_PARSE_ERROR = Symbol(`JS URL failed to parse`);

interface JsUrlParseResult {
  correctedUrl: string; // JSURL string transformed to repair common link malformations by email clients
  data: any; // JSON data parsed from JSURL string; this is always null if valid = false
  valid: boolean;
}

/** Encode an object as URL-safe RISON, dropping the surrounding parens. */
export function encodeRison(params) {
  const risonHash = rison.urlencode(params);
  return risonHash.substring(1, risonHash.length - 1);
}

/** Decode a rison string, not wrapped in parens, into an object. */
export function decodeRison(risonStr): Record<string, any> {
  return rison.urldecode(`(${risonStr})`);
}

/**
 * Attempts to parse a JSURL string into structured data,
 * accounting for potential deformation of the JSURL link-fragment in email
 * clients and other auto-link mechanisms.
 */
export function parseJsUrl(jsurl: string): JsUrlParseResult {
  if (!jsurl) {
    return {correctedUrl: ``, data: null, valid: false};
  }

  let correctedUrl = ``;
  let data = null;

  for (const transform of JS_URL_TRANSFORMS) {
    correctedUrl = transform(jsurl);
    data = JSURL.tryParse(correctedUrl, JS_URL_PARSE_ERROR);
    if (data !== JS_URL_PARSE_ERROR) {
      break;
    }
  }

  if (data !== JS_URL_PARSE_ERROR) {
    return {correctedUrl, data, valid: true};
  } else {
    return {correctedUrl, data: null, valid: false};
  }
}

/**
 * Attempts to serialize a JSON object into a JSURL-encoded string,
 * falling back to a specified defaultValue if the input is null/undefined
 */
export function serializeJsUrl(jsUrlData, defaultValue = ``): string {
  return isNil(jsUrlData) ? defaultValue : JSURL.stringify(jsUrlData);
}

export function encodeQueryParams(params: Record<string, any>) {
  return Object.entries(params)
    .map(([k, v]) => encodeURIComponent(k) + `=` + encodeURIComponent(v))
    .join(`&`);
}
